/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Device Error Detection Utilities for IndexedDB Operations
 *
 * Classifies IndexedDB errors into device/browser limitations vs application errors.
 * Defaults to logging uncertain cases to Sentry to avoid hiding real bugs.
 */

interface DeviceErrorInfo {
  /** Whether this error is caused by device/browser limitations */
  isDeviceError: boolean;
  /** Specific category of the error */
  category:
    | 'quota'
    | 'private-mode'
    | 'permission'
    | 'user-action'
    | 'hardware'
    | 'transient'
    | 'unknown';
  /** Whether this error should be logged to Sentry */
  shouldLogToSentry: boolean;
  /** Additional context about the error */
  details: {
    errorName?: string;
    browserContext?: string;
    payloadSize?: number;
    isReasonablePayload?: boolean;
    detectionReason?: string;
  };
}

// 6MB threshold for "reasonable" payloads - anything below this is likely a device limitation
const REASONABLE_PAYLOAD_THRESHOLD_BYTES = 6 * 1024 * 1024;

/**
 * Detect quota exceeded errors across all browsers
 * Based on W3C IndexedDB specification and browser implementations
 */
function isQuotaExceededError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorObj = error as any;
  const errorMessage = (errorObj.message || String(error)).toLowerCase();
  const errorName = (errorObj.name || '').toLowerCase();
  const errorCode = errorObj.code;

  // Standard QuotaExceededError
  if (errorName === 'quotaexceedederror') {
    return true;
  }

  // DOMException code 22 (QuotaExceededError)
  if (errorCode === 22) {
    return true;
  }

  // Firefox-specific quota error
  if (
    errorName === 'ns_error_dom_quota_reached' ||
    errorMessage.includes('ns_error_dom_quota_reached')
  ) {
    return true;
  }

  // Generic quota-related messages
  const quotaPatterns = [
    'quota',
    'storage space',
    'disk space',
    'exceeded',
    'quota_err',
    'quota_exceeded_err',
    'out of space',
    'storage limit',
  ];

  return quotaPatterns.some((pattern) => errorMessage.includes(pattern));
}

/**
 * Detect private browsing/incognito mode errors
 * Notes:
 * - Safari private mode often throws QuotaExceededError on small writes.
 * - Do NOT treat insecure context as private mode.
 */
function isPrivateBrowsingError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorObj = error as any;
  const errorMessage = (errorObj.message || String(error)).toLowerCase();
  const errorName = (errorObj.name || '').toLowerCase();

  // Firefox InvalidStateError in private browsing (historical)
  if (
    errorName === 'invalidstateerror' &&
    (errorMessage.includes('private') || errorMessage.includes('mutations in private mode'))
  ) {
    return true;
  }

  // SecurityError with private/incognito indicators
  if (
    errorName === 'securityerror' &&
    (errorMessage.includes('idbfactory') ||
      errorMessage.includes('private') ||
      errorMessage.includes('incognito'))
  ) {
    return true;
  }

  // Chrome/Safari private mode indicators
  const privateModePatterns = [
    "failed to execute 'open' on 'idbfactory'",
    'private browsing',
    'incognito',
  ];

  return privateModePatterns.some((pattern) => errorMessage.includes(pattern));
}

/**
 * Detect permission and security restriction errors
 * - If the context is insecure (http), treat as app/env misconfiguration → log to Sentry.
 */
function isPermissionError(error: unknown, ctx?: {isSecureContext?: boolean}): boolean {
  if (!error) {
    return false;
  }

  const errorObj = error as any;
  const errorMessage = (errorObj.message || String(error)).toLowerCase();
  const errorName = (errorObj.name || '').toLowerCase();

  if (errorName === 'notallowederror') {
    return true;
  }

  if (errorName === 'securityerror') {
    // If page is not secure, this is an app/env issue, not a user-permission device error.
    if (ctx?.isSecureContext === false) {
      return false;
    }

    const permissionPatterns = [
      'denied permission',
      'invalid security context',
      'the operation is insecure',
      'permission denied',
      'access denied',
      'blocked by privacy',
    ];
    return permissionPatterns.some((pattern) => errorMessage.includes(pattern));
  }

  return false;
}

/**
 * Detect errors caused by user actions (database deletion, storage clearing)
 * Only classify as user-action when message clearly indicates it.
 */
function isUserInitiatedError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorObj = error as any;
  const errorMessage = (errorObj.message || String(error)).toLowerCase();
  const errorName = (errorObj.name || '').toLowerCase();

  // NotFoundError is user action only when message proves storage/database was removed
  if (errorName === 'notfounderror') {
    const userActionPatterns = ['storage cleared', 'database deleted'];
    if (userActionPatterns.some((p) => errorMessage.includes(p))) {
      return true;
    }
    return false;
  }

  // Do NOT blanket-handle AbortError as user-action
  return false;
}

/**
 * Detect hardware or browser limitation errors
 * Do NOT include DataCloneError here (that is an application misuse).
 */
function isHardwareLimitationError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorObj = error as any;
  const errorMessage = (errorObj.message || String(error)).toLowerCase();
  const errorName = (errorObj.name || '').toLowerCase();

  // UnknownError often indicates hardware/disk issues
  if (errorName === 'unknownerror' || errorName === 'unknown') {
    const hardwarePatterns = ['disk', 'i/o', 'hardware', 'internal error', 'device', 'memory'];
    if (hardwarePatterns.some((pattern) => errorMessage.includes(pattern))) {
      return true;
    }
  }

  // Memory pressure indicators
  const memoryPatterns = [
    'out of memory',
    'memory pressure',
    'insufficient memory',
    'cannot allocate',
  ];
  return memoryPatterns.some((pattern) => errorMessage.includes(pattern));
}

/**
 * Detect transient browser errors that may resolve themselves
 * Treat AbortError as transient so it can be logged or sampled upstream.
 */
function isTransientBrowserError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorObj = error as any;
  const errorMessage = (errorObj.message || String(error)).toLowerCase();
  const errorName = (errorObj.name || '').toLowerCase();

  // BlockedError - database temporarily blocked (non-standard but seen)
  if (errorName === 'blockederror') {
    return true;
  }

  // TimeoutError - operation timed out
  if (errorName === 'timeouterror') {
    return true;
  }

  // AbortError - transaction aborted, can be app or env; treat as transient
  if (errorName === 'aborterror') {
    return true;
  }

  const transientPatterns = [
    'database that did not allow mutations',
    'temporarily blocked',
    'operation timed out',
    'retry',
    'temporary',
  ];
  return transientPatterns.some((pattern) => errorMessage.includes(pattern));
}

/**
 * Detect clear application/schema/usage mistakes
 * Always log these to Sentry.
 */
function isSchemaOrUsageError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  const name = ((error as any).name || '').toLowerCase();

  return [
    'constrainterror', // unique key/index violation
    'dataerror', // invalid key/value
    'transactioninactiveerror',
    'readonlyerror',
    'invalidaccesserror',
    'versionerror', // open/upgrade mismatch
    'typeerror',
    'syntaxerror',
    'datacloneerror', // trying to store non-structured-cloneable value
    // Note: InvalidStateError is handled carefully below via message heuristics
  ].includes(name);
}

/**
 * Calculate the approximate byte size of a payload
 * Accounts for JSON serialization and UTF-16 to UTF-8 conversion
 */
function calculatePayloadSizeInBytes(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }

  try {
    const jsonString = JSON.stringify(value);
    return new TextEncoder().encode(jsonString).length;
  } catch {
    const stringified = String(value);
    return new TextEncoder().encode(stringified).length;
  }
}

/**
 * Main function to analyze any error and determine if it's a device limitation
 */
function analyzeDeviceError(error: unknown, payloadSizeBytes?: number): DeviceErrorInfo {
  if (!error) {
    return {
      isDeviceError: false,
      category: 'unknown',
      shouldLogToSentry: true,
      details: {},
    };
  }

  const errorName = (error as Error)?.name || 'Unknown';
  const errorMessage = ((error as any)?.message || String(error)).toLowerCase();
  const secure = typeof isSecureContext === 'boolean' ? isSecureContext : undefined;

  const details: DeviceErrorInfo['details'] = {
    errorName,
    payloadSize: payloadSizeBytes,
  };

  // 1) Known app/schema/usage mistakes → always log
  if (isSchemaOrUsageError(error)) {
    return {
      isDeviceError: false,
      category: 'unknown',
      shouldLogToSentry: true,
      details: {
        ...details,
        detectionReason: 'Schema/usage error',
      },
    };
  }

  // 2) Quota exceeded
  if (isQuotaExceededError(error)) {
    const isReasonablePayload =
      typeof payloadSizeBytes === 'number'
        ? payloadSizeBytes <= REASONABLE_PAYLOAD_THRESHOLD_BYTES
        : true;

    return {
      isDeviceError: isReasonablePayload,
      category: 'quota',
      shouldLogToSentry: !isReasonablePayload, // large payloads look like app behavior
      details: {
        ...details,
        isReasonablePayload,
        detectionReason: 'Quota exceeded',
      },
    };
  }

  // 3) Private browsing/incognito
  if (isPrivateBrowsingError(error)) {
    return {
      isDeviceError: true,
      category: 'private-mode',
      shouldLogToSentry: false,
      details: {
        ...details,
        browserContext: 'private/incognito',
        detectionReason: 'Private browsing mode limitation',
      },
    };
  }

  // 4) Permission/security restrictions
  if (isPermissionError(error, {isSecureContext: secure})) {
    return {
      isDeviceError: true,
      category: 'permission',
      shouldLogToSentry: false,
      details: {
        ...details,
        detectionReason: 'User privacy/security settings',
      },
    };
  }

  // 5) InvalidStateError needs nuance: private-mode patterns handled above.
  if ((error as any).name?.toLowerCase?.() === 'invalidstateerror') {
    // If message hints at closing connection or upgrade flow misuse, treat as app error.
    const appPatterns = ['connection is closing', 'database connection is closing'];
    if (appPatterns.some((p) => errorMessage.includes(p))) {
      return {
        isDeviceError: false,
        category: 'unknown',
        shouldLogToSentry: true,
        details: {
          ...details,
          detectionReason: 'InvalidStateError due to connection/transaction misuse',
        },
      };
    }
  }

  // 6) User-initiated (only when proven by message)
  if (isUserInitiatedError(error)) {
    return {
      isDeviceError: true,
      category: 'user-action',
      shouldLogToSentry: false,
      details: {
        ...details,
        detectionReason: 'User cleared storage or deleted database',
      },
    };
  }

  // 7) Hardware/browser limitations
  if (isHardwareLimitationError(error)) {
    return {
      isDeviceError: true,
      category: 'hardware',
      shouldLogToSentry: false,
      details: {
        ...details,
        detectionReason: 'Device hardware or browser limitation',
      },
    };
  }

  // 8) Transient errors (including AbortError)
  if (isTransientBrowserError(error)) {
    return {
      isDeviceError: true,
      category: 'transient',
      shouldLogToSentry: true, // transient but valuable to see patterns; consider sampling upstream
      details: {
        ...details,
        detectionReason: 'Temporary browser condition or aborted transaction',
      },
    };
  }

  // 9) Default: treat as application error
  return {
    isDeviceError: false,
    category: 'unknown',
    shouldLogToSentry: true,
    details: {
      ...details,
      detectionReason: 'Unrecognized error pattern - treating as application error',
    },
  };
}

const deviceErrorUtils = {
  isQuotaExceededError,
  isPrivateBrowsingError,
  isPermissionError,
  isUserInitiatedError,
  isHardwareLimitationError,
  isTransientBrowserError,
  isSchemaOrUsageError,
  calculatePayloadSizeInBytes,
  analyzeDeviceError,
  REASONABLE_PAYLOAD_THRESHOLD_BYTES,
};

export {deviceErrorUtils, type DeviceErrorInfo};
